/**
 * RequirePermission Guard
 *
 * Route guard that requires user to have specific permission(s).
 * Redirects to unauthorized page if permission is missing.
 *
 * @example
 * // Single permission
 * <RequirePermission permission={Permissions.Fleet.Vehicles.Read}>
 *   <VehiclesPage />
 * </RequirePermission>
 *
 * @example
 * // Multiple permissions (any)
 * <RequirePermission
 *   permissions={[Permissions.Fleet.Vehicles.Read, Permissions.Fleet.Vehicles.Create]}
 *   requireAll={false}
 * >
 *   <VehiclesPage />
 * </RequirePermission>
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermission, usePermissions } from '../hooks/usePermissions';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface RequirePermissionProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

export const RequirePermission: React.FC<RequirePermissionProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback,
  redirectTo,
}) => {
  const navigate = useNavigate();

  // Single permission check
  const hasSinglePermission = usePermission(permission || '');

  // Multiple permissions check
  const { hasAny, hasAll } = usePermissions(permissions || []);

  // Determine if user has required permission(s)
  const hasPermission = permission
    ? hasSinglePermission
    : requireAll
    ? hasAll
    : hasAny;

  // If no permission, show fallback or redirect
  if (!hasPermission) {
    if (fallback) {
      return <>{fallback}</>;
    }

    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // Default unauthorized UI
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <Typography variant="h4" color="error">
          Neovlašćen pristup
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Nemate dozvolu za pristup ovoj stranici.
        </Typography>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Nazad
        </Button>
      </Box>
    );
  }

  // User has permission, render children
  return <>{children}</>;
};
